// Types and helpers shared by the Team and Buyer Profiles configuration pages.

export type ConnectionStatus = 'pending' | 'active' | 'declined' | 'ended';
export type LicenseStatus = 'pending' | 'approved' | 'rejected';
export type LicenseType = 'real_estate' | 'attorney';

export interface TeamConnection {
  id: string;
  status: ConnectionStatus;
  my_role: 'buyer' | 'agent';
  other_user: { id: string; name: string; email: string };
  signing_authority: boolean;
  initiated_by_me: boolean;
  can_accept: boolean;
  accepted_at: string | null;
  updated_at: string;
}

export interface ProfessionalLicense {
  id: string;
  license_type: LicenseType;
  state: string;
  license_number: string;
  status: LicenseStatus;
  rejection_reason: string | null;
  reviewed_at: string | null;
}

export type ProfileValue = string | number | boolean | null;
// One connected agent as shown on a buyer profile's Connections tab.
export interface ProfileShare {
  agent_id: string;
  name: string;
  email: string;
  shared: boolean;
  contracts_made: number;
}

export type BuyerProfileValues = Record<string, ProfileValue>;

export interface BuyerProfile extends BuyerProfileValues {
  id: string;
  label: string;
  is_default: boolean;
  ready_for_contracts: boolean;
}

export const LICENSE_TYPE_LABELS: Record<LicenseType, string> = {
  real_estate: 'Real estate license',
  attorney: 'Law license (attorney)',
};

export const LICENSE_STATUS_LABELS: Record<LicenseStatus, string> = {
  pending: 'Waiting for review',
  approved: 'Approved',
  rejected: 'Not approved',
};

export const CONNECTION_STATUS_LABELS: Record<ConnectionStatus, string> = {
  pending: 'Pending',
  active: 'Connected',
  declined: 'Declined',
  ended: 'Ended',
};

// Pulls readable messages out of the error shapes our API returns.
export const getErrorMessages = (error: unknown): string[] => {
  const data = (error as { response?: { data?: { errors?: unknown } } })?.response?.data;
  const errors = data?.errors;
  if (Array.isArray(errors)) return errors.map(String);
  if (errors && typeof errors === 'object') {
    return Object.entries(errors as Record<string, string[]>).flatMap(([field, msgs]) =>
      (Array.isArray(msgs) ? msgs : [ String(msgs) ]).map((m) => `${field.replace(/_/g, ' ')} ${m}`));
  }
  return [ 'Something went wrong. Please try again.' ];
};

type FieldType = 'text' | 'number' | 'date' | 'select' | 'checkbox' | 'textarea';

export interface ProfileField {
  name: string;
  label: string;
  type: FieldType;
  options?: { value: string; label: string }[];
  help?: string;
  required?: boolean;
}

export interface ProfileSection {
  title: string;
  description?: string;
  fields: ProfileField[];
}

const PAYER_OPTIONS = [ { value: 'seller', label: 'Seller' }, { value: 'buyer', label: 'Buyer' } ];

export const PROFILE_SECTIONS: ProfileSection[] = [
  {
    title: 'Buyer identity and notices',
    fields: [
      { name: 'buyer_name', label: 'Buyer name (as it prints on the contract)', type: 'text', required: true },
      {
        name: 'entity_type', label: 'Buyer type', type: 'select', options: [
          { value: 'individual', label: 'Individual' }, { value: 'llc', label: 'LLC' },
          { value: 'corporation', label: 'Corporation' }, { value: 'trust', label: 'Trust' },
          { value: 'other', label: 'Other' },
        ],
      },
      { name: 'signer_name', label: 'Signer name (if an entity)', type: 'text' },
      { name: 'signer_title', label: 'Signer title (if an entity)', type: 'text' },
      { name: 'notice_street', label: 'Notice street address', type: 'text', required: true },
      { name: 'notice_city', label: 'City', type: 'text', required: true },
      { name: 'notice_state', label: 'State (2 letters)', type: 'text', required: true },
      { name: 'notice_zip', label: 'Zip', type: 'text', required: true },
      { name: 'phone', label: 'Phone', type: 'text', required: true },
      { name: 'email', label: 'Email', type: 'text', required: true },
      { name: 'fax', label: 'Fax (optional)', type: 'text' },
    ],
  },
  {
    title: 'Earnest money and option',
    fields: [
      {
        name: 'earnest_money_type', label: 'Earnest money is', type: 'select', options: [
          { value: 'flat', label: 'A flat amount ($)' }, { value: 'percent', label: 'A percent of the price (%)' },
        ],
      },
      { name: 'earnest_money_value', label: 'Earnest money amount or percent', type: 'number', required: true },
      { name: 'additional_earnest_money', label: 'Additional earnest money ($)', type: 'number' },
      { name: 'additional_earnest_money_days', label: 'Days to deliver additional earnest money', type: 'number' },
      { name: 'option_fee', label: 'Option fee ($)', type: 'number', required: true },
      { name: 'option_days', label: 'Option period (days)', type: 'number', required: true },
      { name: 'escrow_agent_name', label: 'Escrow agent / title company', type: 'text', required: true },
      { name: 'escrow_agent_address', label: 'Escrow agent address', type: 'text' },
      { name: 'escrow_agent_phone', label: 'Escrow agent phone', type: 'text' },
      { name: 'escrow_agent_email', label: 'Escrow agent email', type: 'text' },
      { name: 'title_company_name', label: 'Title company (if different)', type: 'text' },
    ],
  },
  {
    title: 'Title and survey',
    fields: [
      { name: 'title_policy_paid_by', label: 'Title policy paid by', type: 'select', options: PAYER_OPTIONS },
      {
        name: 'survey_option', label: 'Survey', type: 'select', options: [
          { value: 'existing', label: 'Seller\'s existing survey' }, { value: 'new', label: 'New survey' },
          { value: 'none', label: 'No survey' },
        ],
      },
      { name: 'survey_paid_by', label: 'Survey paid by', type: 'select', options: PAYER_OPTIONS },
      { name: 'survey_days', label: 'Days to get the survey', type: 'number' },
      { name: 'title_objection_days', label: 'Days to object to title and survey', type: 'number' },
    ],
  },
  {
    title: 'Condition, closing and terms',
    fields: [
      { name: 'as_is', label: 'Buyer accepts the property "As Is"', type: 'checkbox' },
      {
        name: 'closing_type', label: 'Closing date is', type: 'select', options: [
          { value: 'days_after_effective', label: 'A number of days after the effective date' },
          { value: 'fixed_date', label: 'A fixed date' },
        ],
      },
      { name: 'closing_days', label: 'Days after effective date', type: 'number' },
      { name: 'closing_date', label: 'Fixed closing date', type: 'date' },
      {
        name: 'possession', label: 'Possession', type: 'select', options: [
          { value: 'closing_and_funding', label: 'On closing and funding' },
          { value: 'temporary_lease', label: 'Under a temporary lease' },
        ],
      },
      {
        name: 'seller_disclosure', label: 'Seller\'s disclosure', type: 'select', options: [
          { value: 'received', label: 'Buyer has received it' }, { value: 'will_receive', label: 'Buyer will receive it' },
          { value: 'not_required', label: 'Buyer does not require it' },
        ],
      },
      { name: 'residential_service_contract', label: 'Include a residential service contract', type: 'checkbox' },
      { name: 'residential_service_paid_by', label: 'Residential service contract paid by', type: 'select', options: PAYER_OPTIONS },
      { name: 'seller_concessions', label: 'Seller concessions ($)', type: 'number' },
      { name: 'special_provisions', label: 'Standard special provisions', type: 'textarea', help: 'Added to every contract, for example: Buyer may assign this contract.' },
    ],
  },
  {
    title: 'Financing (leave as Cash if you are not using a loan)',
    description: 'A cash buyer does not need the Third Party Financing Addendum, so it is left out. Choose a loan type to have it filled in.',
    fields: [
      {
        name: 'financing_type', label: 'How you pay', type: 'select', options: [
          { value: 'cash', label: 'Cash' }, { value: 'conventional', label: 'Conventional loan' },
          { value: 'texas_veterans', label: 'Texas Veterans Land Board loan' }, { value: 'fha', label: 'FHA insured loan' },
          { value: 'va', label: 'VA guaranteed loan' }, { value: 'usda', label: 'USDA guaranteed loan' },
          { value: 'reverse', label: 'Reverse mortgage' },
        ],
      },
      { name: 'loan_percent_of_price', label: 'Loan amount (% of the price)', type: 'number', help: 'For example 80 for an 80% loan. The dollar amount is worked out from each offer price.' },
      { name: 'loan_term_years', label: 'Loan term (years)', type: 'number' },
      { name: 'loan_max_interest_rate', label: 'Interest rate not to exceed (%)', type: 'number' },
      { name: 'loan_rate_years', label: 'Years that rate applies', type: 'number' },
      { name: 'loan_max_origination_percent', label: 'Origination charges not to exceed (% of loan)', type: 'number' },
      { name: 'fha_section', label: 'FHA Section number (FHA loans only)', type: 'text' },
    ],
  },
  {
    title: 'HOA (used when the property has an HOA addendum)',
    fields: [
      { name: 'hoa_association', label: 'Association name and phone (if you know it)', type: 'text', help: 'Usually left blank; it varies by property.' },
      {
        name: 'hoa_info_option', label: 'HOA information (Subdivision Information)', type: 'select', options: [
          { value: 'seller_delivers', label: 'Seller gets it and delivers it within a number of days' },
          { value: 'buyer_delivers', label: 'Buyer gets it within a number of days' },
          { value: 'received_no_update', label: 'Buyer already has it, no updated resale certificate' },
          { value: 'received_update', label: 'Buyer already has it, wants an updated resale certificate' },
          { value: 'not_required', label: 'Buyer does not require it' },
        ],
      },
      { name: 'hoa_info_days', label: 'Days (for the first two choices)', type: 'number' },
      { name: 'hoa_fee_cap', label: 'Buyer pays HOA fees and reserves up to ($)', type: 'number' },
      { name: 'hoa_title_paid_by', label: 'If no information is needed, who pays the title company to get it', type: 'select', options: PAYER_OPTIONS },
    ],
  },
  {
    title: 'Agent (when a licensed professional acts for this buyer)',
    fields: [
      { name: 'agent_name', label: 'Agent name', type: 'text' },
      { name: 'agent_license_number', label: 'Agent license number', type: 'text' },
      { name: 'brokerage_name', label: 'Brokerage', type: 'text' },
      { name: 'brokerage_license_number', label: 'Brokerage license number', type: 'text' },
      { name: 'agent_phone', label: 'Agent phone', type: 'text' },
      { name: 'agent_email', label: 'Agent email', type: 'text' },
      { name: 'agent_compensation_percent', label: 'Agent commission asked of seller (%)', type: 'number', help: 'Filled into page 7, 12.B(1) when an agent makes the offer for this buyer.' },
    ],
  },
];

// Starting values for a brand new profile (match the database defaults).
export const emptyBuyerProfile = (): BuyerProfileValues => ({
  label: '',
  is_default: false,
  entity_type: 'individual',
  earnest_money_type: 'flat',
  title_policy_paid_by: 'seller',
  survey_option: 'existing',
  survey_paid_by: 'buyer',
  financing_type: 'cash',
  as_is: true,
  closing_type: 'days_after_effective',
  possession: 'closing_and_funding',
  seller_disclosure: 'not_required',
  residential_service_contract: false,
});

// Blank text boxes are sent as "no value" rather than empty text.
export const buildProfilePayload = (values: BuyerProfileValues): BuyerProfileValues => {
  const allowed = new Set([ 'label', 'is_default', ...PROFILE_SECTIONS.flatMap((s) => s.fields.map((f) => f.name)) ]);
  const payload: BuyerProfileValues = {};
  Object.entries(values).forEach(([ key, value ]) => {
    if (!allowed.has(key)) return;
    payload[key] = typeof value === 'string' && value.trim() === '' ? null : value;
  });
  return payload;
};
